import { NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X, User, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-200"
    >
      <div className="container-app flex items-center justify-between h-16">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
          <span className="inline-block w-2 h-6 bg-gold" />
          <span className="font-display text-2xl leading-none text-navy">
            Scholar<span className="text-royal">Print</span>Hub
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-navy" : "text-slate-600 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user && user !== false ? (
            <>
              {user.role === "admin" && (
                <Link data-testid="nav-admin" to="/admin" className="btn-outline !py-2 !text-xs">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              )}
              <Link data-testid="nav-dashboard" to="/dashboard" className="btn-outline !py-2 !text-xs">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button
                data-testid="logout-button"
                onClick={logout}
                className="text-slate-600 hover:text-navy p-2"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" className="btn-outline !py-2 !text-xs">
                <User className="w-4 h-4" /> Login
              </Link>
              <Link data-testid="nav-register" to="/register" className="btn-primary !py-2 !text-xs">
                Sign up
              </Link>
            </>
          )}
        </div>

        <Button
          data-testid="nav-toggle"
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <nav className="container-app py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? "text-navy bg-slate-50" : "text-slate-600"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-3 border-t border-slate-200">
              {user && user !== false ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-outline flex-1 !py-2">Dashboard</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="btn-primary flex-1 !py-2">Admin</Link>
                  )}
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline flex-1 !py-2">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="btn-primary flex-1 !py-2">Sign up</Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}

      {/* SR-only brand for testing */}
      <span className="sr-only">{BRAND}</span>
    </header>
  );
}
